import { useCallback, useMemo } from 'react';
import { ScrollView, Platform, ActivityIndicator, Linking } from 'react-native';
import { ScreenHeader } from '@/components/layout/ScreenHeader';
import { Text } from '@/components/gluestack/text';
import { View } from '@/components/gluestack/view';
import { VStack } from '@/components/gluestack/vstack';
import { Card } from '@/components/gluestack/card';
import { Button, ButtonText } from '@/components/gluestack/button';
import { Heading } from '@/components/gluestack/heading';
import { useCommunity } from '@/hooks/useCommunity';
import { useTranslation } from 'react-i18next';
import { MessageView } from '@/components/ui/MessageView';
import { SettingsError01Icon } from '@hugeicons-pro/core-stroke-rounded';
import { HugeIconsWrapper } from '@/components/ui/HugeIconsWrapper';
import PlanCard from '@/components/domain/account/PlanCard';
import ContextMenu from '@/components/ui/ContextMenu';
import { MenuItem, MenuItemLabel } from '@/components/gluestack/menu';
import { router, useFocusEffect } from 'expo-router';
import { PremiumFeatures } from '@/components/domain/account/PremiumFeatures';
import { PremiumStatusCard } from '@/components/domain/account/PremiumStatusCard';
import { useIAPContext } from '@/components/providers/IAPProvider';
import { useSubscribeInfo } from '@/hooks/useSubscribeInfo';
import { BillingLinksList, useBillingLinks } from '@/components/domain/account/BillingLinks';
import { StorePlansTerms } from '@/components/domain/account/StorePlansTerms';
import { useRegisterRightPanelSlot } from '@/components/layout/RightPanelSlotContext';
import { useIsRightPanelVisible } from '@/components/layout/LargeScreenRightPanel';

export default function PlansScreen() {
  const { t } = useTranslation();
  const community = useCommunity();

  const { data: subscribeInfo } = useSubscribeInfo();

  const {
    connected,
    subscriptions,
    isProcessing,
    connectionError,
    activeSubscriptionId,
    handlePurchase,
    handleManageSubscription,
    reconcile,
  } = useIAPContext();

  // Recover any owned-but-unfinished subscription for the active community when the user opens this screen, so a
  // stuck purchase resolves by the time they look at it. This is also where we attempt cross-account orphan
  // recovery (recoverOrphans) — a stranded user goes to Plans, and subscribeInfo is already loaded here, so we
  // gate on it (only when not premium) for free instead of taxing every app-open reconcile with a billing call.
  // Depend on the derived BOOLEAN (not the subscribeInfo object): a not-premium status arriving after focus still
  // triggers recovery, but the routine refetch-on-focus (new object identity, same gate) doesn't re-run reconcile.
  const needsOrphanRecovery = subscribeInfo != null && !subscribeInfo.IsPremium;
  useFocusEffect(
    useCallback(() => {
      if (connected && community) reconcile(needsOrphanRecovery);
    }, [connected, community, reconcile, needsOrphanRecovery])
  );

  const monthlyProduct = subscriptions.find((sub) => sub.id.includes('01'));
  const yearlyProduct = subscriptions.find((sub) => sub.id.includes('12'));
  const currency = monthlyProduct?.currency || 'USD';

  const hidePlansExceptActive = (subscribeInfo?.IsPremium || !!activeSubscriptionId);

  const billingLinks = useBillingLinks();
  // Wide large screens (tablets in landscape) move the billing links and terms to the right panel,
  // once the store is connected (not next to the connecting or connection-error states)
  const showRightPanel = useIsRightPanelVisible() && connected;
  const rightPanelContent = useMemo(
    () =>
      connected ? (
        <VStack space="3xl">
          <BillingLinksList />
          <View>
            <Text className="mb-4 text-sm font-bold uppercase text-tertiary-500">
              {t('account.plans.terms')}
            </Text>
            <StorePlansTerms currency={currency} />
          </View>
        </VStack>
      ) : null,
    [connected, t, currency]
  );
  useRegisterRightPanelSlot(rightPanelContent);

  const isActivePlan = (productId: string | null | undefined): boolean => {
    return !!productId && activeSubscriptionId === productId;
  };

  const shouldRenderProduct = (productId: string | null | undefined): boolean => {
    return !hidePlansExceptActive || isActivePlan(productId ?? undefined);
  };

  return (
    <View className="flex-1">
      <ScreenHeader
        options={{
          headerShown: true,
          title: t('account.plans.title'),
          headerRight: () =>
            showRightPanel ? null : (
              <ContextMenu>
                {billingLinks.map((link) => (
                  <MenuItem
                    key={link.href}
                    textValue={link.label}
                    onPress={() => router.push(link.href)}>
                    <MenuItemLabel>{link.label}</MenuItemLabel>
                  </MenuItem>
                ))}
              </ContextMenu>
            ),
        }}
      />

      {!connected ? (
        connectionError ? (
          <MessageView
            type="error"
            text={t('account.plans.connectionError')}
            subText={connectionError}
            icon={SettingsError01Icon}
          >
            <VStack space="sm" className="w-full max-w-md px-4">
              {Platform.OS === 'android' ? (
                <>
                  <Button
                    variant="solid"
                    action="primary"
                    onPress={async () => {
                      try {
                        await Linking.openURL('market://details?id=com.android.vending');
                      } catch {
                        await Linking.openURL('https://play.google.com/store/apps/details?id=com.android.vending');
                      }
                    }}
                  >
                    <ButtonText>{t('account.plans.openPlayStore')}</ButtonText>
                  </Button>
                  
                  <Text size="xs" className="text-center text-typography-500">
                    {t('account.plans.signInToPlayStore')}
                  </Text>
                </>
              ) : (
                <>
                  <Button
                    variant="solid"
                    action="primary"
                    onPress={async () => {
                      try {
                        await Linking.openURL('https://apps.apple.com/account/subscriptions');
                      } catch (error) {
                        console.error('Failed to open App Store settings:', error);
                      }
                    }}
                  >
                    <ButtonText>{t('account.plans.openAppStore')}</ButtonText>
                  </Button>
                  
                  <Text size="xs" className="text-center text-typography-500">
                    {t('account.plans.checkAppStoreConnection')}
                  </Text>
                </>
              )}
            </VStack>
          </MessageView>
        ) : (
          <MessageView
            type="info"
            text={t('account.plans.connecting')}
          >
            <ActivityIndicator size="large" />
          </MessageView>
        )
      ) : (
        <ScrollView className="bg-background-page">
          <VStack className="mx-auto w-full max-w-4xl p-4" space="4xl">
            {/* Premium status first, ahead of features and plans */}
            {subscribeInfo?.IsPremium && subscribeInfo?.PremiumEndsOnUtc && community?.premiumPlan && (
              <PremiumStatusCard
                premiumEndsOnUtc={subscribeInfo.PremiumEndsOnUtc}
                premiumPlanName={community.premiumPlan}
              />
            )}

            <PremiumFeatures />

            <VStack space="md">
              <Heading size="md" className="ml-1 text-typography-700">
                {t('account.plans.choosePlan')}
              </Heading>

              {isProcessing && (
                <Card size="md">
                  <VStack className="items-center justify-center py-6" space="md">
                    <ActivityIndicator size="large" />
                    <Text className="text-typography-500">{t('account.plans.confirming')}</Text>
                  </VStack>
                </Card>
              )}

              {subscriptions.length === 0 ? (
                <Card size="md">
                  <VStack className="items-center justify-center py-8" space="md">
                    {connectionError ? (
                      <>
                        <HugeIconsWrapper icon={SettingsError01Icon} size={32} className="color-error-700" />
                        <Text className="text-typography-700 text-center">{connectionError}</Text>
                      </>
                    ) : (
                      <>
                        <ActivityIndicator size="large" />
                        <Text className="text-typography-500">{t('account.plans.loading')}</Text>
                      </>
                    )}
                  </VStack>
                </Card>
              ) : (
                <VStack space="md">
                  {monthlyProduct && shouldRenderProduct(monthlyProduct.id) && (
                    <PlanCard
                      product={monthlyProduct}
                      planType="monthly"
                      onPress={() => handlePurchase(monthlyProduct.id)}
                      isCurrentPlan={isActivePlan(monthlyProduct.id)}
                      isProcessing={isProcessing}
                    />
                  )}

                  {yearlyProduct && shouldRenderProduct(yearlyProduct.id) && (
                    <PlanCard
                      product={yearlyProduct}
                      planType="yearly"
                      isRecommended
                      onPress={() => handlePurchase(yearlyProduct.id)}
                      isCurrentPlan={isActivePlan(yearlyProduct.id)}
                      isProcessing={isProcessing}
                    />
                  )}
                </VStack>
              )}

              <Button
                variant="outline"
                action="neutral"
                onPress={handleManageSubscription}
                disabled={isProcessing}
              >
                <ButtonText>{t('account.plans.manageSubscription')}</ButtonText>
              </Button>
            </VStack>

            {/* Terms (in the right panel when it's shown) */}
            {!showRightPanel && (
              <VStack space="md">
                <Heading size="md" className="ml-1 text-typography-700">
                  {t('account.plans.terms')}
                </Heading>
                <Card size="md">
                  <StorePlansTerms currency={currency} />
                </Card>
              </VStack>
            )}
          </VStack>
        </ScrollView>
      )}
    </View>
  );
}
